<script>
    import Animation from './Animation.svelte';
    import SceneSelector from "./uiElements/SceneSelector.svelte";
    import {getEndLabel, getStartLabel, getSubLabel, scenes} from "./timelines/main-timeline";
    import {mainTimeline} from "./timelines/main-timeline";
    import AboutResearch from "./uiElements/overlays/AboutResearch.svelte";
    import InfoController from "./uiElements/info-boxes/InfoController.svelte";
    import ThoughtLeadership from "./uiElements/overlays/ThoughtLeadership.svelte";
    import ThoughtWinsBusiness from "./uiElements/overlays/ThoughtWinsBusiness.svelte";
    import DownloadPage from "./uiElements/overlays/DownloadPage.svelte";
    import Subtitles from "./pre-comps/Elements/Subtitles.svelte";
    import Audio from "./uiElements/Audio.svelte";
    import MuteButton from "./pre-comps/Elements/MuteButton.svelte";
    import ScrollController from "./uiElements/ScrollController.svelte";
    import PageTitles from "./uiElements/PageTitles.svelte";
    import ChartButton from "./uiElements/ChartButton.svelte";
    import ReadingThoughtLeadership from "./uiElements/overlays/ReadingThoughtLeadership.svelte";
    import GnosisHitAreas from "./uiElements/GnosisHitAreas.svelte";
    import CornerGnosis from "./pre-comps/CornerGnosis.svelte";
    import isMobile from "./helpers/isMobile";
    import OpeningTitles from "./uiElements/OpeningTitles.svelte";
    import DownloadIcon from "./uiElements/DownloadIcon.svelte";
    import ControlledAudio from "./uiElements/ControlledAudio.svelte";

    let playheadPosition = 0;
    let subPlayheadPosition = 0;
    let researchOpen = false;
    let thoughtLeadershipOpen = false;
    let thoughtWinsBusinessOpen = false;
    let readingThoughLeadershipOpen = false;
    let readGuideOpen = false;
    let audioMuted = isMobile();
    let interacted = false;
    let wasPausedWhenOverlayOpened;

    let downloadSfx;
    let openSfx;
    let sceneSfx;
    let subSceneSfx;

    function playSubSceneSfx() {
        subSceneSfx && subSceneSfx.play();
    }

    function playOpenSfx() {
        openSfx && openSfx.play();
    }

    function playSceneSfx() {
        sceneSfx && sceneSfx.play();
    }

    function playDownloadSfx() {
        downloadSfx && downloadSfx.play();
    }

    function toggleAudioMuted() {
        audioMuted = !audioMuted;
    }

    $: overlayOpen = researchOpen
        || thoughtLeadershipOpen
        || thoughtWinsBusinessOpen
        || readGuideOpen
        || readingThoughLeadershipOpen
    ;

    $: if (overlayOpen) {
        wasPausedWhenOverlayOpened = mainTimeline.paused();
        if (!wasPausedWhenOverlayOpened) {
            mainTimeline.pause();
        }
    }

    $: if (!overlayOpen) {
        if (!wasPausedWhenOverlayOpened) {
            mainTimeline.play();
        }
    }

    $: showAboutResearch = !overlayOpen && playheadPosition === 13;
    $: showDownloadReport = !overlayOpen && playheadPosition < 3;
    $: showDownloadIconText = playheadPosition === 3;

    function handleGoToSubScene(event) {
        playSubSceneSfx();
        if (event.detail.subScene !== 0) {
            mainTimeline.seek(getSubLabel(playheadPosition, event.detail.subScene - 1), false)
            mainTimeline.play();
        } else {
            mainTimeline.seek(getSubLabel(playheadPosition, event.detail.subScene), false)
            mainTimeline.pause();
        }
        subPlayheadPosition = event.detail.subScene;
    }

    function handleGoToScene(event) {
        playSceneSfx();
        playheadPosition = event.detail.scene;
        subPlayheadPosition = 0;
        mainTimeline.seek(getStartLabel(playheadPosition), true)
        mainTimeline.play();
    }

    function closeAnyOverlay() {
        researchOpen = false;
        thoughtLeadershipOpen = false;
        thoughtWinsBusinessOpen = false;
        readingThoughLeadershipOpen = false;
        readGuideOpen = false;
    }

    function handleResetSite() {
        playSceneSfx();
        subPlayheadPosition = 0;
        playheadPosition = 0;

        mainTimeline.seek(getStartLabel(playheadPosition));
        closeAnyOverlay();
        mainTimeline.play();
    }

    function handleNext() {
        if (overlayOpen) return;
        if (playheadPosition !== 1 && playheadPosition !== 2) {
            playSceneSfx();
        }
        subPlayheadPosition = 0;
        mainTimeline.seek(getEndLabel(playheadPosition), false)
        mainTimeline.play();
        if (playheadPosition < scenes.length - 1) {
            playheadPosition = playheadPosition + 1;
        }
        interacted = true;
    }

    function handlePrevious() {
        if (overlayOpen) return;
        playSceneSfx();
        subPlayheadPosition = 0;

        if (playheadPosition !== 0) {
            playheadPosition = playheadPosition - 1;
        }

        mainTimeline.seek(getStartLabel(playheadPosition));
        mainTimeline.play();
    }

    function handleNextWithSubScene() {
        if (overlayOpen) return;
        if (interacted && playheadPosition < scenes.length - 1) {

            const pauses = Boolean(scenes[playheadPosition]) ? scenes[playheadPosition].pauses || 0 : 0;

            if (!Boolean(pauses) || subPlayheadPosition >= pauses - 1) {
                subPlayheadPosition = 0;
                handleNext();
            } else {
                playSubSceneSfx();
                mainTimeline.seek(getSubLabel(playheadPosition, subPlayheadPosition), false)
                mainTimeline.play();
                subPlayheadPosition = subPlayheadPosition + 1;
            }
        }
    }

    function handleResetSubscene() {
        if (overlayOpen) return;
        if (playheadPosition !== 0) {
            const pauses = Boolean(scenes[playheadPosition]) ? scenes[playheadPosition].pauses || 0 : 0;

            if (Boolean(pauses) && subPlayheadPosition > 0) {
                playSubSceneSfx();
                subPlayheadPosition = subPlayheadPosition - 1;
                mainTimeline.seek(getSubLabel(playheadPosition, subPlayheadPosition), false)
            } else {
                handlePrevious();
                subPlayheadPosition = 0;
            }
        }
    }

    function handleResearchClick() {
        playOpenSfx();
        researchOpen = !researchOpen;
    }

    function handleReadingLeadershipClick() {
        playOpenSfx();
        readingThoughLeadershipOpen = !readingThoughLeadershipOpen;
    }

    function handleToggleThoughtLeadership() {
        playOpenSfx();
        thoughtLeadershipOpen = !thoughtLeadershipOpen;
    }

    function handleToggleThoughtWinsBusiness() {
        playOpenSfx();
        thoughtWinsBusinessOpen = !thoughtWinsBusinessOpen;
    }

    function handleToggleReadGuide() {
        playDownloadSfx()
        readGuideOpen = !readGuideOpen;
    }

</script>

<nav>
    <h1 class="logo-heading">
        <button on:click={handleResetSite}>
            <img class="logo-img" src="images/ft-longi-logo.png" alt="Longitude, a Financial Times company">
        </button>
    </h1>
    {#if thoughtLeadershipOpen}
        <button on:click={handleToggleThoughtLeadership}><img src="images/close.svg" alt="Close overlay"></button>
    {/if}
    {#if thoughtWinsBusinessOpen}
        <button on:click={handleToggleThoughtWinsBusiness}><img src="images/close.svg" alt="Close overlay"></button>
    {/if}
    {#if readGuideOpen}
        <button on:click={handleToggleReadGuide}><img src="images/close.svg" alt="Close overlay"></button>
    {/if}
    {#if readingThoughLeadershipOpen}
        <button on:click={handleReadingLeadershipClick}><img src="images/close.svg" alt="Close overlay"></button>
    {/if}
    {#if researchOpen}
        <button on:click={handleResearchClick}><img src="images/close.svg" alt="Close overlay"></button>
    {/if}
    {#if showAboutResearch}
        <button on:click={handleResearchClick}>About the research</button>
    {/if}
    {#if showDownloadReport}
        <button on:click={handleToggleReadGuide} class="download-report-button">DOWNLOAD THE GUIDE</button>
    {/if}
</nav>

{#if researchOpen}
    <section class="overlay">
        <div class="overlay-inner">
            <AboutResearch/>
        </div>
    </section>
{/if}

{#if readGuideOpen}
    <section class="overlay">
        <div class="overlay-inner download-page">
            <DownloadPage/>
        </div>
    </section>
{/if}

{#if thoughtLeadershipOpen}
    <section class="overlay">
        <div class="overlay-inner thought-leadership">
            <ThoughtLeadership/>
        </div>
    </section>
{/if}

{#if thoughtWinsBusinessOpen}
    <section class="overlay">
        <div class="overlay-inner thought-leadership">
            <ThoughtWinsBusiness/>
        </div>
    </section>
{/if}

{#if readingThoughLeadershipOpen}
    <section class="overlay">
        <div class="overlay-inner reading-thought-leadership">
            <ReadingThoughtLeadership/>
        </div>
    </section>
{/if}

<CornerGnosis/>
<PageTitles/>
<OpeningTitles/>

<ChartButton on:click={handleReadingLeadershipClick}/>
{#if playheadPosition > 0}
    <div class="mobile-overlay-buttons">
        {#if playheadPosition === 5}
            <button on:click={handleToggleThoughtLeadership}>
                <img src="images/plus.svg" alt="next" class="plus-icon">
            </button>
        {/if}
        {#if playheadPosition === 11}
            <button on:click={handleToggleThoughtWinsBusiness}>
                <img src="images/plus.svg" alt="next" class="plus-icon">
            </button>
        {/if}
        {#if playheadPosition === 13}
            <button on:click={handleToggleReadGuide}>
                <img src="images/download-icon.svg" alt="next" class="plus-icon">
            </button>
        {/if}
    </div>
{/if}

<Subtitles/>

<main>
    <div class="animation">
        <Animation scene={playheadPosition} audioMuted={audioMuted} on:go-to-scene={handleGoToScene}/>
        <GnosisHitAreas scene={playheadPosition} on:go-to-sub-scene={handleGoToSubScene}
                        on:go-to-scene={handleGoToScene}/>
    </div>
</main>

<div class="scene-selector-container">
    {#if playheadPosition < 3}
        <h3 class="start-here">Start here</h3>
        <button class="continue-button pulse" on:click={handleNext}>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="30" viewBox="0 0 48 30">
                <path id="fticon-v1_arrow-right" d="M6,0,0,6,18,24,0,42l6,6L30,24Z" transform="translate(48) rotate(90)"
                      class="arrow-down"/>
            </svg>
        </button>
    {:else }
        <SceneSelector
                on:next={handleNext}
                on:previous={handlePrevious}
                on:home={handleResetSite}
                sceneCount={scenes.length}
                currentScene={playheadPosition}
                audioMuted={audioMuted}
        />
    {/if}

    {#if playheadPosition > 0}
        <div class="info-boxes">
            <InfoController
                    scene={playheadPosition}
                    subScene={subPlayheadPosition}
                    audioMuted={audioMuted}
                    on:toggle_thought_leadership={handleToggleThoughtLeadership}
                    on:toggle_thought_business={handleToggleThoughtWinsBusiness}
                    on:toggle_read_guide={handleToggleReadGuide}
                    on:advance_sub_scene={handleNextWithSubScene}
            />
        </div>
    {/if}
</div>

<ScrollController
        on:next={handleNextWithSubScene}
        on:previous={handleResetSubscene}
/>


<div class="lower-controls">
    {#if !isMobile()}
        <Audio audioMuted={audioMuted}/>
        <MuteButton on:click={toggleAudioMuted} audioMuted={audioMuted}/>
    {/if}

    {#if !overlayOpen && !showDownloadReport}
        <button on:click={handleToggleReadGuide} class="all-page-download-button" class:dl-after={showDownloadIconText}>
            <DownloadIcon orange={false}/>
        </button>
    {/if}
</div>

<ControlledAudio bind:this={sceneSfx} src="audio/nav-click-2.mp3" audioMuted={audioMuted}/>
<ControlledAudio bind:this={downloadSfx} src="audio/download.mp3" audioMuted={audioMuted}/>
<ControlledAudio bind:this={openSfx} src="audio/thought-leadership-click.mp3" audioMuted={audioMuted}/>
<ControlledAudio bind:this={subSceneSfx} src="audio/info-box-next.mp3" audioMuted={audioMuted}/>

<style>

    .arrow-down {
        fill: #fff;
    }

    button:hover .arrow-down {
        fill: #FF8833
    }

    .overlay {
        background-color: black;
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        padding: 150px 0 50px 0;
        z-index: 6;
    }

    .overlay-inner {
        max-width: 900px;
        margin: 0 auto;
        height: auto;
        padding-left: 40px;
    }

    .reading-thought-leadership {
        min-height: calc(100vh - 200px);
    }

    .download-page {
        min-height: calc(100vh - 200px);
    }

    nav {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: 50px;
        color: white;
        z-index: 7;
    }

    .logo-heading {
        margin: 0;
    }

    .logo-img {
        width: 220px;
    }

    main {
        margin: 0 auto;
        position: relative;
        z-index: 1;
    }

    .info-boxes {
        display: inline-block;
        position: absolute;
        bottom: 80px;
        right: 80px;
        width: 300px;
        z-index: 3;
    }

    .scene-selector-container {
        position: fixed;
        bottom: 0;
        width: 100%;
        z-index: 2;
        padding-bottom: 80px;
    }

    .start-here {
        color: white;
        font-weight: 400;
        font-size: 30px;
        text-align: center;
        margin: 0;
    }

    .continue-button {
        margin: 0 auto;
        display: block;
    }

    .pulse {
        animation: pulse 2s linear infinite;
    }

    @keyframes pulse {
        0% {
            transform: scale(1);
        }

        10% {
            transform: scale(1.1);
        }

        50% {
            transform: scale(1);
        }

        100% {
            transform: scale(1);
        }
    }

    button {
        display: inline-block;
        border: none;
        padding: 1rem 2rem;
        margin: 0;
        text-decoration: none;
        background: none;
        color: #ffffff;
        font-family: Metric, sans-serif;
        font-size: 1rem;
        cursor: pointer;
        text-align: center;
        transition: background 250ms ease-in-out,
        transform 150ms ease;
        -webkit-appearance: none;
        -moz-appearance: none;
    }

    button:hover,
    button:focus {
        background: none;
        color: #FF8833;
    }

    button:focus {
        outline: none;
        outline-offset: -4px;
    }

    button:active:not(:disabled) {
        transform: scale(0.98);
    }

    button:disabled {
        opacity: 0.6;
        cursor: default;
    }

    .mobile-overlay-buttons {
        display: none;
        position: absolute;
        top: 290px;
        left: 62%;
        z-index: 5;
    }

    .download-report-button {
        border: 1px white solid;
    }

    .download-report-button:hover {
        border-color: #FF8833;
    }

    @media (min-width: 640px) {
        main {
            max-width: 1400px;
        }
    }

    @media (max-width: 639px) {
        .mobile-overlay-buttons {
            display: inline-block;
        }

        nav {
            padding: 1rem;
        }

        main {
            transform: scale(1.6);
            transform-origin: center;
            margin-top: 3rem;
        }

        nav button {
            padding: 0;
            align-self: flex-start;
        }

        .logo-img {
            width: 140px;
        }

        .info-boxes {
            display: none;
        }
    }

    .lower-controls {
        position: fixed;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        top: calc(100vh - 70px);
        left: 20px;
        height: 66px;
        z-index: 4;
    }

    .all-page-download-button {
        transform: scale(0.85);
        transform-origin: center;
        padding-left: 20px;
        display: flex;
        justify-items: flex-start;
        align-items: center;
        font-size: 16px;
    }

    .dl-after:after {
        content: "Download";
        display: block;
        position: absolute;
        right: -70%;
        top: 20px;
        font-size: 18px;
    }

    @media screen and (max-width: 480px) {
        .lower-controls {
            bottom: -5px;
            left: -15px;
            top: unset;
        }

        .start-here {
            transform: scale(0.6);
        }

        .continue-button {
            transform: scale(0.4);
            transform-origin: bottom;
        }

        .download-report-button {
            font-size: 10px;
            padding: 5px;
        }

    }

    @media screen and (max-width: 850px) {
        .info-boxes {
            right: 40px;
            width: 220px;
        }
    }

    @media screen and (max-width: 940px) {
        .overlay-inner {
            padding: 0 20px;
        }
    }

    @media screen and (max-width: 1440px) {
        .info-boxes {
            right: 40px;
        }
    }

    @media screen and (max-height: 1050px) {
        nav {
            padding-bottom: 0;
        }
    }

    @media screen and (max-height: 950px) {
        .info-boxes {
            bottom: 0;
        }

        .scene-selector-container {
            padding-bottom: 0;
        }

        main {
            max-width: 1200px;
        }
    }

    @media screen and (max-height: 799px) {
        main {
            max-width: 1000px;
        }
    }
</style>


